import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  SpinnerGap,
  Bell,
  Clock,
  Envelope,
  ChatCircleText,
  Lightning,
  ArrowRight,
  CheckCircle,
  Info,
  ChartLine,
} from "phosphor-react";
import { useEntitlementsWithFetch } from "../contexts/EntitlementContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TIMING_OPTIONS = [
  { value: "1", label: "1 hour before" },
  { value: "2", label: "2 hours before" },
  { value: "4", label: "4 hours before" },
  { value: "12", label: "12 hours before" },
  { value: "24", label: "24 hours before (recommended)" },
  { value: "48", label: "48 hours before" },
  { value: "72", label: "72 hours before" },
];

export default function ReminderSettings() {
  const navigate = useNavigate();
  const { isFeatureActive, isEntitled } = useEntitlementsWithFetch();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notEntitled, setNotEntitled] = useState(false);
  const [featureInactive, setFeatureInactive] = useState(false);
  const [featureDetails, setFeatureDetails] = useState(null);
  const [stats, setStats] = useState(null);
  
  const [settings, setSettings] = useState({
    enabled: false,
    hours_before: 24,
    channels: ["email"],
  });

  useEffect(() => {
    fetchSettings();
    fetchStats();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/tenants/reminder-settings`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.error === "feature_inactive") {
          setFeatureInactive(true);
          setLoading(false);
          return;
        }
        
        if (data.error === "not_entitled") {
          setNotEntitled(true);
          setFeatureDetails(data.feature);
          setLoading(false);
          return;
        }
        
        if (data.entitled) {
          setSettings(data.settings);
        }
      }
    } catch (error) {
      console.error("Error fetching reminder settings:", error);
      toast.error("Failed to load reminder settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/tenants/reminder-stats`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.entitled && data.stats) {
          setStats(data.stats);
        }
      }
    } catch (error) {
      console.error("Error fetching reminder stats:", error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/tenants/reminder-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        toast.success("Reminder settings saved!");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (channel) => {
    setSettings((prev) => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter((c) => c !== channel)
        : [...prev.channels, channel],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-[#FF6600]" />
      </div>
    );
  }

  // Feature is globally inactive - hide everything
  if (featureInactive) {
    return (
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="reminder-settings-inactive">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/settings")}
            className="border-2 border-zinc-200"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
              Appointment Reminders
            </h1>
            <p className="text-zinc-500">Feature not available</p>
          </div>
        </div>
        
        <Card className="border-2 border-zinc-200">
          <CardContent className="p-8 text-center">
            <Bell size={48} className="mx-auto text-zinc-300 mb-4" />
            <h2 className="font-heading text-lg font-bold uppercase mb-2">Coming Soon</h2>
            <p className="text-zinc-500">Appointment reminders are not currently available.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User not entitled - show CTA
  if (notEntitled) {
    return (
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="reminder-settings-not-entitled">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/settings")}
            className="border-2 border-zinc-200"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
              Appointment Reminders
            </h1>
            <p className="text-zinc-500">Reduce no-shows with automated reminders</p>
          </div>
        </div>
        
        <Card className="border-2 border-[#FF6600]/30 bg-gradient-to-r from-[#FF6600]/5 to-transparent">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-16 h-16 rounded-full bg-[#FF6600]/20 flex items-center justify-center flex-shrink-0">
                <Bell size={32} className="text-[#FF6600]" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-xl font-bold uppercase mb-2">
                  Enable Appointment Reminders
                </h2>
                <p className="text-zinc-600 mb-4">
                  Automatically remind your customers about upcoming appointments via email or SMS.
                  Reduce no-shows by up to 50% and keep your schedule running smoothly.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                    <CheckCircle size={12} className="mr-1" />
                    Reduce No-Shows
                  </Badge>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                    <Envelope size={12} className="mr-1" />
                    Email Reminders
                  </Badge>
                  <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                    <ChatCircleText size={12} className="mr-1" />
                    SMS Reminders
                  </Badge>
                </div>
                {featureDetails && (
                  <p className="text-sm text-zinc-500">
                    Starting at <span className="font-bold text-[#FF6600]">${(featureDetails.price_monthly || 0).toFixed(2)}/mo</span>
                  </p>
                )}
              </div>
              <Button
                onClick={() => navigate("/settings/upgrades")}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white px-6"
                data-testid="upgrade-cta-btn"
              >
                <Lightning size={18} className="mr-2" />
                Upgrade Now
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is entitled - show full settings
  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="reminder-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/settings")}
            className="border-2 border-zinc-200"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
              Appointment Reminders
            </h1>
            <p className="text-zinc-500">Automatically remind customers about upcoming appointments</p>
          </div>
        </div>
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
          data-testid="save-settings-btn"
        >
          {saving ? <SpinnerGap size={18} className="animate-spin" /> : "Save Settings"}
        </Button>
      </div>

      {/* Enable Toggle */}
      <Card className="border-2 border-zinc-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <Bell size={20} className="text-[#FF6600]" />
                Reminder Status
              </CardTitle>
              <CardDescription>Enable or disable automatic appointment reminders</CardDescription>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              data-testid="reminders-enabled-switch"
            />
          </div>
        </CardHeader>
        <CardContent>
          {settings.enabled ? (
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-sm">
              <CheckCircle size={20} className="text-emerald-600" />
              <span className="text-sm text-emerald-800">
                Reminders are active. Customers will receive notifications before their appointments.
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-zinc-100 border border-zinc-200 rounded-sm">
              <Info size={20} className="text-zinc-500" />
              <span className="text-sm text-zinc-600">
                Reminders are disabled. No notifications will be sent to customers.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timing */}
      <Card className="border-2 border-zinc-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
            <Clock size={20} className="text-[#FF6600]" />
            Reminder Timing
          </CardTitle>
          <CardDescription>When should reminders be sent before appointments?</CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={settings.hours_before.toString()}
            onValueChange={(v) => setSettings({ ...settings, hours_before: parseInt(v) })}
          >
            <SelectTrigger className="w-full md:w-80" data-testid="timing-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMING_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-zinc-500 mt-2">
            Reminders are sent within 1 hour of this time before the appointment.
          </p>
        </CardContent>
      </Card>

      {/* Channels */}
      <Card className="border-2 border-zinc-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
            <ChatCircleText size={20} className="text-[#FF6600]" />
            Notification Channels
          </CardTitle>
          <CardDescription>
            How should reminders be sent? Respects customer communication preferences.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <label
              className={`flex items-center gap-4 p-4 rounded-sm border-2 cursor-pointer transition-all ${
                settings.channels.includes("email")
                  ? "border-[#FF6600] bg-[#FF6600]/5"
                  : "border-zinc-200 hover:border-zinc-300"
              }`}
            >
              <Checkbox
                checked={settings.channels.includes("email")}
                onCheckedChange={() => toggleChannel("email")}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Envelope size={20} className="text-blue-600" />
                  <span className="font-medium">Email</span>
                </div>
                <p className="text-sm text-zinc-500 mt-1">
                  Send reminder emails to customers. Works with all customers.
                </p>
              </div>
            </label>
            
            <label
              className={`flex items-center gap-4 p-4 rounded-sm border-2 cursor-pointer transition-all ${
                settings.channels.includes("sms")
                  ? "border-[#FF6600] bg-[#FF6600]/5"
                  : "border-zinc-200 hover:border-zinc-300"
              }`}
            >
              <Checkbox
                checked={settings.channels.includes("sms")}
                onCheckedChange={() => toggleChannel("sms")}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <ChatCircleText size={20} className="text-purple-600" />
                  <span className="font-medium">SMS</span>
                </div>
                <p className="text-sm text-zinc-500 mt-1">
                  Send text message reminders. Requires Twilio configuration and customer phone number.
                </p>
              </div>
            </label>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-sm">
            <p className="text-sm text-blue-800">
              <Info size={16} className="inline mr-1" />
              <strong>Customer Preference:</strong> If a customer has set their preferred communication 
              method, we&apos;ll use that channel. Otherwise, email is used by default.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats Section - Only show when enabled and has data */}
      {settings.enabled && stats && (
        <Card className="border-2 border-zinc-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <ChartLine size={20} className="text-[#FF6600]" />
              Reminder Performance
            </CardTitle>
            <CardDescription>Track how your reminders are performing</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-zinc-50 rounded-sm text-center">
                <p className="text-3xl font-bold text-[#FF6600]">{stats.sent_last_7_days}</p>
                <p className="text-xs text-zinc-500 mt-1">Sent (7 days)</p>
              </div>
              <div className="p-4 bg-zinc-50 rounded-sm text-center">
                <p className="text-3xl font-bold text-zinc-800">{stats.sent_last_30_days}</p>
                <p className="text-xs text-zinc-500 mt-1">Sent (30 days)</p>
              </div>
              <div className="p-4 bg-zinc-50 rounded-sm text-center">
                <p className="text-3xl font-bold text-emerald-600">{stats.delivery_rate}%</p>
                <p className="text-xs text-zinc-500 mt-1">Delivery Rate</p>
              </div>
              <div className="p-4 bg-zinc-50 rounded-sm text-center">
                <p className="text-3xl font-bold text-blue-600">{stats.upcoming_24h}</p>
                <p className="text-xs text-zinc-500 mt-1">Pending (24h)</p>
              </div>
            </div>
            {stats.total_sent > 0 && (
              <p className="text-xs text-zinc-400 mt-4 text-center">
                Total reminders sent all-time: {stats.total_sent}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      {settings.enabled && (
        <Card className="border-2 border-dashed border-zinc-300 bg-zinc-50">
          <CardContent className="p-6">
            <h3 className="font-heading text-sm uppercase text-zinc-500 mb-3">Preview</h3>
            <p className="text-zinc-700">
              <Bell size={16} className="inline mr-1 text-[#FF6600]" />
              Customers will receive {settings.channels.join(" and ")} reminder(s){" "}
              <strong>{settings.hours_before} hour(s)</strong> before their scheduled appointments.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
